import React, { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useTheme } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import i18n from './i18n/i18n';
import {
  DefaultCustomRateResponse,
  ProductsRateResponse
} from './store/_declarations/financialCalculator';
import { fetchTokenAsync } from './store/api/auth';
import { useAppDispatch, useAppSelector } from './store/hooks';
import {
  setDownPayment,
  setSelectedItem,
  setUpdateWidget,
  setVisibilityFeatures,
  setWidgetInitializationObject,
  setWidgetType
} from './store/slices/appSlice';
import MainView from './ui/pages/MainView';
import { ShowWidgetProps } from './utils/_declarations/globals';
import bbCodeParsed from './utils/bbCodeParsed';
import { pxToRem } from './utils/styles';

type AppProps = {
  idRoot?: string;
  modalVersion?: boolean;
  testPrupose?: boolean;
  unmountApp: () => void;
  widgetInit: ShowWidgetProps;
};

const elaborateLegalMentions = (
  brandLabel?: string,
  longLabel?: string,
  legalMention?: DefaultCustomRateResponse.LegalMention[]
) => {
  if (!legalMention) {
    return [];
  }
  return {
    legalMention: legalMention.map(
      ({ text }: DefaultCustomRateResponse.LegalMention) => ({
        text: bbCodeParsed(text, brandLabel, longLabel)
      })
    )
  };
};

const createPayloadToPromiseResolve = (
  payload: DefaultCustomRateResponse,
  brandLabel?: string,
  longLabel?: string
) => {
  const globalText = elaborateLegalMentions(
    brandLabel,
    longLabel,
    payload?.result?.globalText?.legalMention
  );

  return {
    ...payload,
    result: {
      ...payload.result,
      globalText
    }
  };
};

const clearIntervalTokenRefresh = () => {
  if (window.tokenRefreshInterval) {
    window.clearInterval(window.tokenRefreshInterval);
    window.tokenRefreshInterval = null;
  }
};

const manageTokenRefresh = (
  creationDate: number,
  expiresIn: number,
  fetchToken: () => void
) => {
  const expDate = new Date(creationDate + expiresIn * 1000);
  const timeout = expDate.valueOf() - new Date().valueOf();
  const timeoutCondition = timeout > 0 ? timeout - 30000 : 0;

  setTimeout(() => {
    fetchToken();

    const refreshInterval: number =
      expiresIn * 1000 < 30000 ? 30000 : expiresIn * 1000 - 30000;
    const interval = window.setInterval(() => {
      fetchToken();
    }, refreshInterval);

    window.tokenRefreshInterval = interval;
  }, timeoutCondition);
};
const App: React.FC<AppProps> = ({
  idRoot,
  modalVersion,
  testPrupose,
  unmountApp,
  widgetInit
}) => {
  const { creationDate, expiresIn, value } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);

  const theme = useTheme();

  useEffect(() => {
    const effectExecution = () => {
      if (!value) {
        dispatch(fetchTokenAsync());
        return;
      }

      if (expiresIn && creationDate) {
        manageTokenRefresh(creationDate, expiresIn, () =>
          dispatch(fetchTokenAsync())
        );
      }
    };
    effectExecution();

    return () => clearIntervalTokenRefresh();
  }, [expiresIn]);

  useEffect(() => {
    const { context, parameters, vehicle } = widgetInit;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { brandName, ...rest } = context;
    const newContext = rest;
    dispatch(
      setWidgetInitializationObject({
        context: newContext,
        parameters,
        vehicle
      })
    );
    const type = modalVersion ? 'modal' : 'embedded';
    dispatch(setWidgetType(type));
  }, [widgetInit]);

  const manageErrorAndDispatch = useCallback((error: any, uuid: string) => {
    if (error.name === 'AbortError') {
      return;
    }
    if (uuid) {
      /* dispatch(postDefaultCustomRateError(error.response)); */
    }
    /*  dispatch(postProductsRateError(error.response)); */
  }, []);

  useEffect(() => {
    const { context } = widgetInit;
    const languageCode = context.languageCode as string;
    i18n.changeLanguage(languageCode);
  }, [widgetInit.context.languageCode]);

  const LeasysEventUpdatedItem = useCallback((e: CustomEventInit) => {
    if (!widgetInit.outputFn) {
      return;
    }
    widgetInit.outputFn(
      e.detail
        .func()
        .then((res: any) => {
          const { brandLabel, longLabel } =
            widgetInit?.vehicle?.presentation || {};

          if (e.detail.uuid) {
            /*  const dcrRes: DefaultCustomRateResponse = {
              ...res,
              uuid: e.detail.uuid
            }; */
            /*   dispatch(updateDefaultCustomRate(dcrRes)); */

            return new Promise((response) => {
              const payload = res;

              const parsedPayload = createPayloadToPromiseResolve(
                payload,
                brandLabel,
                longLabel
              );

              response(parsedPayload);
            });
          }

          const listRes: ProductsRateResponse = {
            ...res,
            packageSelection: res.packageSelection.map(
              (singlePackage: ProductsRateResponse.PackageSelection) => ({
                ...singlePackage,
                uuid: uuidv4()
              })
            )
          };

          if (listRes?.packageSelection?.length > 0) {
            const item = listRes?.packageSelection[0];
            dispatch(setSelectedItem(item.uuid));
            const { deposit, depositWithVat } = item.result.teaser.data;
            dispatch(setDownPayment({ deposit, depositWithVat }));
          }

          /*  dispatch(updateProductsList(listRes)); */

          if (widgetInit?.parameters) {
            return new Promise(() => {});
          }

          return new Promise((response) => {
            const itemSelected = { ...res.packageSelection[0] };

            delete itemSelected.criteriaAdjustment;

            const payload = itemSelected;

            const parsedPayload = createPayloadToPromiseResolve(
              payload,
              brandLabel,
              longLabel
            );

            response(parsedPayload);
          });
        })
        .catch((error: any) => {
          manageErrorAndDispatch(error, e.detail.uuid);

          return new Promise((_, rejected) => {
            const rejectError = new Error(
              'Error in postDefaultCustomRate or postProductsRate'
            );
            rejected(rejectError);
          });
        })
    );
  }, []);

  const LeasysEventUpdateWidget = useCallback((e: CustomEventInit) => {
    const { context, parameters, vehicle } = e.detail;
    dispatch(
      setUpdateWidget({
        context,
        parameters,
        vehicle
      })
    );
  }, []);

  useEffect(() => {
    if (!modalVersion) {
      document.addEventListener(
        'LeasysEventUpdatedItem',
        LeasysEventUpdatedItem
      );
      document.addEventListener(
        'LeasysEventUpdatedWidget',
        LeasysEventUpdateWidget
      );
      document.addEventListener(
        'LeasysEventUpdatedWidget',
        (e: CustomEventInit) => {
          const { context, parameters, vehicle } = e.detail;
          dispatch(
            setWidgetInitializationObject({
              context,
              parameters,
              vehicle
            })
          );
        }
      );
    }

    if (!modalVersion) {
      return () => {
        document.removeEventListener(
          'LeasysEventUpdatedItem',
          LeasysEventUpdatedItem
        );
        document.removeEventListener(
          'LeasysEventUpdatedWidget',
          LeasysEventUpdateWidget
        );
      };
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_VISIBILITY_FEATURES) {
      dispatch(
        setVisibilityFeatures(
          JSON.parse(process.env.REACT_APP_VISIBILITY_FEATURES)
        )
      );
    }
  }, []);

  const closeModal = () => {
    setIsModalOpen(false);
    clearIntervalTokenRefresh();
    unmountApp();
  };

  const renderView = useCallback(
    () => <MainView closeModal={closeModal} outputFn={widgetInit.outputFn} />,
    [widgetInit]
  );

  const mobileModalFixes = theme.mediaQueries.isMobile
    ? { border: 'none', bottom: '0', left: '0', right: '0', top: '0' }
    : {};

  if (modalVersion) {
    return (
      <ReactModal
        ariaHideApp={!testPrupose}
        id={idRoot}
        isOpen={isModalOpen}
        style={{
          content: {
            ...mobileModalFixes,
            margin: 'auto',
            maxWidth: `${pxToRem(1126)}`,
            overflow: 'hidden',
            padding: '0'
          }
        }}
      >
        {renderView()}
      </ReactModal>
    );
  }
  return renderView();
};
export default App;
